import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntForm, Input, Modal, Switch } from 'antd';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import type { Translator } from '@/i18n/config';
import type { Project, ProjectInput } from '../types';

const FORM_ID = 'project-form';
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    name: z.string().min(1, t('form.nameRequired')).max(200),
    slug: z
      .string()
      .min(1, t('form.slugRequired'))
      .max(120)
      .refine((v) => SLUG_RE.test(v), t('form.slugInvalid')),
    domain: z.string().max(255).optional(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

interface ProjectFormModalProps {
  open: boolean;
  editing: Project | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: ProjectInput) => void;
  onUpdate: (id: string, input: ProjectInput) => void;
}

export function ProjectFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: ProjectFormModalProps) {
  const { t } = useTranslation(['projects', 'common']);
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const isEdit = editing !== null;

  const defaultValues: Values = {
    name: editing?.name ?? '',
    slug: editing?.slug ?? '',
    domain: editing?.domain ?? '',
    isDefault: editing?.isDefault ?? false,
    isActive: editing?.isActive ?? true,
  };

  const onSubmit = (values: Values) => {
    const input: ProjectInput = {
      name: values.name.trim(),
      slug: values.slug.trim(),
      domain: values.domain?.trim() ? values.domain.trim() : null,
      isDefault: values.isDefault,
      isActive: values.isActive,
    };
    if (isEdit && editing) onUpdate(editing.id, input);
    else onCreate(input);
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      onCancel={onCancel}
      okText={isEdit ? t('form.save') : t('form.create')}
      cancelText={t('common:actions.cancel')}
      okButtonProps={{ htmlType: 'submit', form: FORM_ID, loading: saving }}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form
        id={FORM_ID}
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        options={{ values: defaultValues }}
      >
        <AntForm layout="vertical" component="div">
          <FormField name="name" label={t('form.name')} required>
            <Input placeholder={t('form.namePlaceholder')} maxLength={200} />
          </FormField>
          <FormField name="slug" label={t('form.slug')} required help={t('form.slugHint')}>
            <Input placeholder={t('form.slugPlaceholder')} maxLength={120} />
          </FormField>
          <FormField name="domain" label={t('form.domain')} help={t('form.domainHint')}>
            <Input placeholder={t('form.domainPlaceholder')} maxLength={255} />
          </FormField>
          <FormField
            name="isDefault"
            label={t('form.isDefault')}
            valueProp="checked"
            help={t('form.isDefaultHint')}
          >
            {/* Varsayılan proje unset EDİLEMEZ (BE invariant) — mevcut varsayılan
                düzenlenirken toggle salt-okunur; başka projeye devretmek için o
                projede isDefault açılır. */}
            <Switch disabled={isEdit && (editing?.isDefault ?? false)} />
          </FormField>
          <FormField name="isActive" label={t('form.isActive')} valueProp="checked">
            <Switch />
          </FormField>
        </AntForm>
      </Form>
    </Modal>
  );
}
